import { getUnixTime } from "date-fns";
// constants
import {
  STOCK_OPTIONS,
  PERIOD_TYPES,
  DISCOUNT_TYPES,
  SHIPPING_DATE_TYPES,
  BILLING_POLICY,
  BILLING_POLICY_PARTIAL_TYPE,
  BILLING_POLICY_FULL_PAYMENT_CHECKOUT_TYPE,
  SETTINGS_TYPE,
  SELLING_PLAN_TYPE,
  INVENTORY_POLICY,
} from "../../common/constants";
// helpers
import { getCurrentDay, getUnixTimeFromStartDay } from "../../utils/date";

export const defaultPreorderConfig = {
  discount: 0,
  discountType: DISCOUNT_TYPES.PERCENT,
  startDate: getCurrentDay(),
  endDate: null,
  hasEndDate: false,
  stock: {
    option: STOCK_OPTIONS.STOCK_2,
    hasUnlimitedQuantity: true,
    quantity: 0,
  },
  shippingDate: {
    type: SHIPPING_DATE_TYPES.NO_SET,
    date: getCurrentDay(),
    periodType: PERIOD_TYPES[0],
    periodValue: 1,
    periodStart: {
      type: PERIOD_TYPES[0],
      value: 1,
    },
    periodEnd: {
      type: PERIOD_TYPES[0],
      value: 1,
    },
  },
  sellingPlan: "",
  sellingPlanName: "Pre-order item delivers as soon as available",
  sellingPlanGroup: "",
  sellingPlanGroupType: SELLING_PLAN_TYPE.CREATE,
  sellingPlanGroupName: "Pre-order",
  sellingPlanGroupDescription: "",
  settingsTemplate: "",
  billingPolicy: {
    type: BILLING_POLICY.FULL_AMOUNT,
    upfrontPayment: {
      type: DISCOUNT_TYPES.PERCENT,
      value: 0,
    },
    finalPayment: {
      type: BILLING_POLICY_PARTIAL_TYPE.DATE,
      date: getCurrentDay(),
      afterCheckout: {
        type: BILLING_POLICY_FULL_PAYMENT_CHECKOUT_TYPE.DAYS,
        value: 1,
      },
    },
  },
  inventoryPolicy: INVENTORY_POLICY.ON_SALE,
  revertOnRemove: true,
};

export const defaultBISConfig = {
  startDate: getCurrentDay(),
  endDate: null,
  hasEndDate: false,
  stock: {
    option: STOCK_OPTIONS.STOCK_2,
    hasUnlimitedQuantity: true,
    quantity: 0,
  },
  settingsTemplate: "",
  threshold: 1,
};

const defaultCSConfig = {
  startDate: getCurrentDay(),
  endDate: null,
  hasEndDate: false,
  stock: {
    option: STOCK_OPTIONS.STOCK_2,
    hasUnlimitedQuantity: true,
    quantity: 0,
  },
  settingsTemplate: "",
};

export const defaultISConfig = {
  discount: 0,
  discountType: DISCOUNT_TYPES.PERCENT,
  startDate: getCurrentDay(),
  endDate: null,
  hasEndDate: false,
  stock: {
    option: STOCK_OPTIONS.STOCK_3,
    hasUnlimitedQuantity: true,
    quantity: 0,
  },
  shippingDate: {
    type: SHIPPING_DATE_TYPES.NO_SET,
    date: getCurrentDay(),
    periodType: PERIOD_TYPES[0],
    periodValue: 1,
    periodStart: {
      type: PERIOD_TYPES[0],
      value: 1,
    },
    periodEnd: {
      type: PERIOD_TYPES[0],
      value: 1,
    },
  },
  settingsTemplate: "",
};

export const defaultProductConfig = {
  isPOEnabled: true,
  isBISEnabled: false,
  isCSEnabled: false,
  isISEnabled: false,
  config: defaultPreorderConfig,
  configBIS: defaultBISConfig,
  configCS: defaultCSConfig,
  configIS: defaultISConfig,
};

const isEndDateActive = (config, currentTime) => {
  return !config.hasEndDate || (config.hasEndDate && config.endDate > currentTime);
};

const getStatusByDate = (config) => {
  const currentTime = getUnixTimeFromStartDay();

  if (config.startDate <= currentTime && isEndDateActive(config, currentTime)) {
    return "ACTIVE";
  }

  if (config.hasEndDate && config.endDate <= currentTime) {
    return "EXPIRED";
  }

  return "PENDING";
};

const isUserQuantityValid = (stock) => {
  return stock.hasUnlimitedQuantity || stock.quantity > 0;
};

const getStatusByStock = (stock, variant) => {
  if (stock.option === "STOCK1") {
    if (isUserQuantityValid(stock)) {
      return "ACTIVE";
    }

    return "NO_STOCK";
  }

  if (stock.option === "STOCK2") {
    if (variant.inventoryQuantity <= 0 && isUserQuantityValid(stock)) {
      return "ACTIVE";
    }

    if (variant.inventoryQuantity <= 0 && !isUserQuantityValid(stock)) {
      return "NO_STOCK";
    }

    return "PENDING";
  }

  if (stock.option === "STOCK3") {
    if (variant.inventoryQuantity > 0) {
      return "ACTIVE";
    }
    return "NO_STOCK";
  }
};

export const computeStatus = (config, variant) => {
  const dateStatus = getStatusByDate(config);

  const stockStatus = getStatusByStock(config.stock, variant);

  if (dateStatus === "ACTIVE") {
    return stockStatus;
  }

  if (dateStatus === "EXPIRED") {
    return dateStatus;
  }

  if (dateStatus === "PENDING") {
    if (stockStatus === "NO_STOCK") {
      return "NO_STOCK";
    }
    return dateStatus;
  }

  return "DISABLED";
};

export const processPreorderConfig = ({
  stock,
  discount,
  discountType,
  startDate,
  hasEndDate,
  endDate,
  shippingDate,
  settingsTemplate = null,
  sellingPlanGroup = null,
  sellingPlan = null,
  billingPolicy,
}) => {
  const config = {
    discount: discount ? discount : 0,
    discountType: discountType,
    startDate: getUnixTime(startDate),
    hasEndDate,
    stock: {
      ...stock,
      option: stock.option.toUpperCase(),
      quantity: stock.quantity > 0 ? parseInt(stock.quantity) : 0,
    },
    shippingDate: {
      type: shippingDate.type,
      date: getUnixTime(shippingDate.date),
      periodValue: parseInt(shippingDate.periodValue),
      periodType: shippingDate.periodType,
      periodStart: {
        value: parseInt(shippingDate.periodStart.value),
        type: shippingDate.periodStart.type,
      },
      periodEnd: {
        value: parseInt(shippingDate.periodEnd.value),
        type: shippingDate.periodEnd.type,
      },
    },
    settingsTemplate,
    sellingPlanGroup,
    sellingPlan,
    billingPolicy: {
      ...billingPolicy,
      finalPayment: {
        ...billingPolicy.finalPayment,
        date: getUnixTime(billingPolicy.finalPayment.date),
        afterCheckout: {
          type: billingPolicy.finalPayment.afterCheckout.type,
          value: parseInt(billingPolicy.finalPayment.afterCheckout.value),
        },
      },
    },
  };

  if (hasEndDate) {
    config.endDate = getUnixTime(endDate);
  }

  return config;
};

export const processISConfig = ({
  stock,
  discount,
  discountType,
  startDate,
  hasEndDate,
  endDate,
  shippingDate,
  settingsTemplate = null,
}) => {
  const config = {
    discount: discount ? parseInt(discount) : 0,
    discountType: discountType,
    startDate: getUnixTime(startDate),
    hasEndDate,
    stock: {
      ...stock,
      option: stock.option.toUpperCase(),
    },
    shippingDate: {
      type: shippingDate.type,
      date: getUnixTime(shippingDate.date),
      periodValue: parseInt(shippingDate.periodValue),
      periodType: shippingDate.periodType,
      periodStart: {
        value: parseInt(shippingDate.periodStart.value),
        type: shippingDate.periodStart.type,
      },
      periodEnd: {
        value: parseInt(shippingDate.periodEnd.value),
        type: shippingDate.periodEnd.type,
      },
    },
    settingsTemplate,
  };

  if (hasEndDate) {
    config.endDate = getUnixTime(endDate);
  }

  return config;
};

export const processBISConfig = ({ stock, startDate, hasEndDate, endDate, settingsTemplate = null, threshold }) => {
  const config = {
    startDate: getUnixTime(startDate),
    hasEndDate,
    stock: {
      ...stock,
      option: stock.option.toUpperCase(),
    },
    settingsTemplate,
    threshold: Number(threshold),
  };

  if (hasEndDate) {
    config.endDate = getUnixTime(endDate);
  }

  return config;
};

export const processCSConfig = ({ stock, startDate, hasEndDate, endDate, settingsTemplate = null }) => {
  const config = {
    startDate: getUnixTime(startDate),
    hasEndDate,
    stock: {
      ...stock,
      option: stock.option.toUpperCase(),
    },
    settingsTemplate,
  };

  if (hasEndDate) {
    config.endDate = getUnixTime(endDate);
  }

  return config;
};

export const extractId = (id) => {
  const splitedId = id.split("/");

  if (splitedId.length === 5) {
    return splitedId[4];
  }

  return id;
};

export const getProductsFromIds = (ids) => {
  return ids.reduce((resources, id) => {
    const productId = id.split("-")[0];
    const variantId = id.split("-")[1];

    if (!resources[productId]) {
      resources[productId] = new Set();
    }

    resources[productId].add(variantId);

    return resources;
  }, {});
};

export const extractPrice = (variant) => {
  const pricing = {
    price: variant.price ? parseFloat(variant.price) : 0,
    compareAtPrice: 0,
  };

  if (variant.compareAtPrice && parseFloat(variant.compareAtPrice) > pricing.price) {
    pricing.compareAtPrice = parseFloat(variant.compareAtPrice);
  }

  return pricing;
};

export const createProduct = ({ product, config, variantIds }) => {
  const defaultConfig = {
    config: processPreorderConfig(defaultPreorderConfig),
    configBIS: processBISConfig(defaultBISConfig),
    configCS: processCSConfig(defaultCSConfig),
    configIS: processISConfig(defaultISConfig),
  };

  const preOrderProduct = {
    handle: product.handle,
    quantity: product.totalInventory || 0,
    // ? product.totalInventory
    // : product.variants.reduce(
    //   (acc, variant) => acc + variant.inventoryQuantity,
    //   0
    // ) || 0,
    title: product.title || "",
    img: product.images && Array.isArray(product.images) && product.images.length > 0 ? product.images[0].src : "",
    vendor: product.vendor || "",
    tags: product.tags || [],
    type: "MANUAL",
  };

  if (product.variants && product.variants.length > 0) {
    // Has variants
    preOrderProduct.variants = {};
    product.variants.forEach((variant) => {
      preOrderProduct.variants[variant.id] = {
        quantity: variant.inventoryQuantity || 0,
        price: variant.price ? parseFloat(variant.price) : 0,
        title: variant.title || "",
        displayName: variant.displayName.endsWith("Default Title") ? product.title : variant.displayName || "",
        inventoryManagement: variant.inventoryManagement ? variant.inventoryManagement.toUpperCase() : "SHOPIFY",
        inventoryPolicy: variant.inventoryPolicy.toUpperCase() || "DENY",
        sku: variant.sku || "",
        isPOEnabled: variantIds.has(String(variant.id)) ? config.isPOEnabled : false,
        isBISEnabled: variantIds.has(String(variant.id)) ? config.isBISEnabled : false,
        isCSEnabled: variantIds.has(String(variant.id)) ? config.isCSEnabled : false,
        isISEnabled: variantIds.has(String(variant.id)) ? config.isISEnabled : false,
        config: variantIds.has(String(variant.id))
          ? {
              ...config.config,
              status: config.isPOEnabled ? computeStatus(config.config, variant) : "NO_SET",
            }
          : {
              ...defaultConfig.config,
              status: "NO_SET",
            },
        configBIS: variantIds.has(String(variant.id))
          ? {
              ...config.configBIS,
              status: config.isBISEnabled ? computeStatus(config.configBIS, variant) : "NO_SET",
            }
          : {
              ...defaultConfig.configBIS,
              status: "NO_SET",
            },
        configCS: variantIds.has(String(variant.id))
          ? {
              ...config.configCS,
              status: config.isCSEnabled ? computeStatus(config.configCS, variant) : "NO_SET",
            }
          : {
              ...defaultConfig.configCS,
              status: "NO_SET",
            },
        configIS: variantIds.has(String(variant.id))
          ? {
              ...config.configIS,
              status: config.isISEnabled ? computeStatus(config.configIS, variant) : "NO_SET",
            }
          : {
              ...defaultConfig.configIS,
              status: "NO_SET",
            },
      };
    });
  } else {
    console.log("[Error] no variants: ", product);
  }

  return preOrderProduct;
};

export const getProductsByType = (type, state) => {
  const configTypes = {
    PO: "isPOEnabled",
    BIS: "isBISEnabled",
    CS: "isCSEnabled",
    IS: "isISEnabled",
  };

  return Object.values(state).filter((entry) =>
    Object.values(entry.variants).some((variant) => {
      if ("isPOEnabled" in variant) {
        return variant[configTypes[type]];
      }
      // [BACKWARDS COMPATIBILITY] There is only Pre-order config for this product.
      return type === "PO";
    })
  );
};

export const updateVariantConfigs = (entry, type) => {
  if ("isPOEnabled" in entry) {
    if (type === SETTINGS_TYPE.PO) {
      entry.isPOEnabled = false;
      entry.config.status = "NO_SET";
    }

    if (type === SETTINGS_TYPE.BIS) {
      entry.isBISEnabled = false;
      entry.configBIS.status = "NO_SET";
    }

    if (type === SETTINGS_TYPE.CS) {
      entry.isCSEnabled = false;
      entry.configCS.status = "NO_SET";
    }

    if (type === SETTINGS_TYPE.IS) {
      entry.isISEnabled = false;
      entry.configIS.status = "NO_SET";
    }
  } else {
    // [BACKWARDS COMPATIBILITY] There is no BIS or CS config for this product.
    entry.config.status = "NO_SET";
  }
};
